import { Pressable, View, type ViewStyle } from 'react-native';
import { CopyPlus } from 'lucide-react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { cva } from 'class-variance-authority';
import { Icon } from '~/global/components/icon/Icon';
import { WorkoutCompletionStatus, WorkoutUtils } from '@fytness/shared';
import { WorkoutRecord } from '~/powersync/generated/schema';
import { useColor } from '~/global/hooks/useColor';
import { RowMenu } from '@/components/ui/row-menu/RowMenu';
import Svg, { Circle } from 'react-native-svg';

export interface WorkoutItemProps extends WorkoutRecord {
  id: string;
  exerciseCount: number;
  onPress?: () => void;
  /**
   * When provided, the row exposes a Duplicate action (⋮ on web / long-press on
   * native). Omit it (e.g. in chat tooling) to render a plain, menu-less row.
   */
  onDuplicate?: () => void;
}

const RING_SIZE = 48;
const RING_STROKE = 4;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// Static so NativeWind's css-interop never re-processes the row's className on re-render.
// A changing container className collapses the row's Fabric layout to zero height on iOS New
// Arch (the `isCreating` flip when a workout finishes syncing is the trigger), which piled
// every affected row's ring into a diagonal cascade. The dynamic bits — the STARTED accent
// border and the isCreating opacity — are applied via inline style instead (see below).
const CONTAINER_CLASS =
  'flex flex-row items-center gap-3.5 rounded-xl bg-surface p-4';

const metaVariants = cva('font-inter-regular text-xs', {
  variants: {
    status: {
      [WorkoutCompletionStatus.FINISHED]: 'text-positive',
      [WorkoutCompletionStatus.STARTED]: 'text-text-secondary',
      [WorkoutCompletionStatus.PLANNED]: 'text-text-muted',
    },
  },
});

const badgeBgVariants = cva('rounded-md px-3 py-1.5', {
  variants: {
    status: {
      [WorkoutCompletionStatus.FINISHED]: 'bg-positive-soft',
      [WorkoutCompletionStatus.STARTED]: 'bg-accent',
      [WorkoutCompletionStatus.PLANNED]: 'bg-surface-elevated',
    },
  },
});

const badgeTextVariants = cva('font-inter-semibold text-xs', {
  variants: {
    status: {
      [WorkoutCompletionStatus.FINISHED]: 'text-positive',
      [WorkoutCompletionStatus.STARTED]: 'text-accent-text',
      [WorkoutCompletionStatus.PLANNED]: 'text-text-primary',
    },
  },
});

function ProgressRing({ status }: { status: WorkoutCompletionStatus }) {
  const positiveColor = useColor('positive');
  const accentColor = useColor('accent');
  const borderColor = useColor('border');
  const mutedColor = useColor('text-muted');

  const ringColorMap = {
    [WorkoutCompletionStatus.FINISHED]: positiveColor,
    [WorkoutCompletionStatus.STARTED]: accentColor,
    [WorkoutCompletionStatus.PLANNED]: borderColor,
  };

  const iconColorMap = {
    [WorkoutCompletionStatus.FINISHED]: positiveColor,
    [WorkoutCompletionStatus.STARTED]: accentColor,
    [WorkoutCompletionStatus.PLANNED]: mutedColor,
  };

  const strokeDashoffset =
    status === WorkoutCompletionStatus.FINISHED ? 0 : RING_CIRCUMFERENCE;

  return (
    <View
      className="relative items-center justify-center"
      style={{ width: RING_SIZE, height: RING_SIZE }}
    >
      <Svg
        width={RING_SIZE}
        height={RING_SIZE}
        style={{ position: 'absolute' }}
      >
        <Circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          stroke={borderColor}
          strokeWidth={RING_STROKE}
          fill="none"
        />
        {status !== WorkoutCompletionStatus.PLANNED && (
          <Circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_RADIUS}
            stroke={ringColorMap[status]}
            strokeWidth={RING_STROKE}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={strokeDashoffset}
            rotation={-90}
            origin={`${RING_SIZE / 2}, ${RING_SIZE / 2}`}
          />
        )}
      </Svg>
      {status === WorkoutCompletionStatus.FINISHED ? (
        <Icon name="Check" size={20} color={iconColorMap[status]} />
      ) : (
        <Icon name="Dumbbell" size={20} color={iconColorMap[status]} />
      )}
    </View>
  );
}

export const WorkoutItem = ({
  id,
  name,
  duration,
  exerciseCount,
  onPress,
  onDuplicate,
  ...workout
}: WorkoutItemProps) => {
  const completionStatus = WorkoutUtils.getCompletionStatus(workout);
  const { t } = useTranslation('workout');
  const isCreating = !workout.niceId;

  const metaParts = [
    t('exercises', { count: exerciseCount }),
    t('duration', { duration }),
  ];
  if (completionStatus === WorkoutCompletionStatus.FINISHED) {
    metaParts.push(t('status.finished'));
  }
  const metaText = metaParts.join(' • ');

  const actionLabel = isCreating
    ? t('status.creating')
    : t(`action.${completionStatus.toLowerCase()}`);

  const accentColor = useColor('accent');

  // Dynamic container styling via inline style (NOT className) — see CONTAINER_CLASS.
  const containerStyle: ViewStyle = {
    opacity: isCreating ? 0.5 : 1,
    ...(completionStatus === WorkoutCompletionStatus.STARTED
      ? { borderWidth: 1, borderColor: accentColor }
      : null),
  };

  const rowContent = (
    <>
      <ProgressRing status={completionStatus} />

      <View className="flex-1 gap-0.5">
        <Text
          className="font-inter-medium text-sm text-text-primary"
          numberOfLines={1}
        >
          {name}
        </Text>
        <Text className={metaVariants({ status: completionStatus })}>
          {metaText}
        </Text>
      </View>

      <View className={badgeBgVariants({ status: completionStatus })}>
        <Text className={badgeTextVariants({ status: completionStatus })}>
          {actionLabel}
        </Text>
      </View>
    </>
  );

  if (onDuplicate) {
    return (
      <RowMenu
        testID="workout-list-item"
        className={CONTAINER_CLASS}
        style={containerStyle}
        disabled={isCreating}
        triggerPlacement="inline"
        onSelect={onPress ?? (() => {})}
        actionsLabel={t('duplicate.actionsLabel')}
        actions={[
          {
            key: 'duplicate',
            label: t('duplicate.action'),
            icon: CopyPlus,
            systemImage: 'plus.square.on.square',
            onPress: onDuplicate,
            testID: 'workout-row-duplicate',
          },
        ]}
      >
        {rowContent}
      </RowMenu>
    );
  }

  return (
    <Pressable
      testID="workout-list-item"
      onPress={isCreating ? undefined : onPress}
      disabled={isCreating}
      className={CONTAINER_CLASS}
      style={containerStyle}
    >
      {rowContent}
    </Pressable>
  );
};
